/**
 * Test utility to simulate character-by-character streaming of markdown content
 * This helps identify rendering boundaries that cause markdown parse errors or visual jumps
 *
 * Usage in browser console:
 *
 * import { simulateStreaming } from './lib/testStreamingRenderer';
 *
 * const markdown = "# Test\n\nThis is **bold** text with a [link](https://example.com)";
 * simulateStreaming(markdown, {
 *   intervalMs: 50,  // Delay between characters
 *   onUpdate: (partial) => console.log('Partial:', partial)
 * });
 */

/**
 * Simulates character-by-character streaming of text
 * @param {string} fullText - The complete text to stream
 * @param {Object} options
 * @param {number} options.intervalMs - Milliseconds between each character (default: 50)
 * @param {Function} options.onUpdate - Callback called with partial text after each character
 * @param {Function} options.onComplete - Callback called when streaming completes
 * @param {Function} options.onError - Callback called if an error occurs during rendering
 * @returns {Object} - Controller object with stop() method
 */
export const simulateStreaming = (fullText, options = {}) => {
  const {
    intervalMs = 50,
    onUpdate = () => {},
    onComplete = () => {},
    onError = () => {}
  } = options;

  let currentIndex = 0;
  let stopped = false;

  const interval = setInterval(() => {
    if (stopped || currentIndex >= fullText.length) {
      clearInterval(interval);
      if (currentIndex >= fullText.length && !stopped) {
        onComplete(fullText);
      }
      return;
    }

    currentIndex++;
    const partialText = fullText.substring(0, currentIndex);

    try {
      onUpdate(partialText, currentIndex, fullText.length);
    } catch (error) {
      onError(error, partialText, currentIndex);
      clearInterval(interval);
    }
  }, intervalMs);

  return {
    stop: () => {
      stopped = true;
      clearInterval(interval);
    },
    getCurrentIndex: () => currentIndex,
    isComplete: () => currentIndex >= fullText.length
  };
};

/**
 * Test markdown with common patterns that can break during partial rendering
 */
export const TEST_MARKDOWN_SAMPLES = {
  simple: "Hello **world**!",

  withLinks: "Check out [this link](https://example.com) and [another](https://test.com).",

  withList: `Here's a list:
- Item 1
- Item 2 with **bold**
- Item 3`,

  withTable: `| Column 1 | Column 2 |
|----------|----------|
| Cell 1   | Cell 2   |
| Cell 3   | Cell 4   |`,

  withCode: "Here's some `inline code` and:\n```javascript\nconst x = 42;\n```",

  withHeaders: `# H1 Header
## H2 Header
### H3 Header

Some text here.`,

  complex: `# Complex Example

Here's some **bold** and *italic* text.

## A list with links:
- First item with a [link](https://example.com)
- Second item with \`code\`
- Third item

## A table:
| Name | Value |
|------|-------|
| A    | 1     |
| B    | 2     |

And some more text at the end.`,

  llmThinking: `Let me think about this problem.

<thinking>
This is internal reasoning that should be styled differently.
It might span multiple lines.
</thinking>

Here's my answer: **42**`,

  nested: `Here's a paragraph with **bold text containing [a link](https://example.com) inside** it.

> And a blockquote with *emphasized [linked text](https://test.com)* within.`,

  // Edge cases that historically caused issues
  incompleteList: `Here's a list:
- Item 1
- Item 2 with **bold**
- Incomplete item without newline`,

  numberedListAfterColon: `Here's what to do:
1. First step
2. Second step
3. Third step`,

  listWithLongFP: `Values:
- Pi: 3.14159265359
- E: 2.71828182846
- Phi: 1.61803398875`,

  mixedMarkdown: `# Header

Regular paragraph with **bold** and *italic*.

- List item 1
- List item 2

| Table | Test |
|-------|------|
| A     | 1    |

> Quote

Final paragraph.`,

  llmTagsInList: `Steps:
1. <thinking>Consider the problem</thinking>First analyze
2. <thinking>Find solution</thinking>Then implement
3. <thinking>Verify</thinking>Finally test`,

  consecutiveNewlines: `Paragraph 1


Paragraph 2 with multiple newlines above`,

  trailingDash: `Here's a list:
-`,
};

/**
 * Find all character positions where markdown structure changes
 * (useful for identifying problem boundaries)
 */
export const findMarkdownBoundaries = (markdown) => {
  const boundaries = [];
  const patterns = [
    { name: 'bold_start', regex: /\*\*/g },
    { name: 'italic_start', regex: /\*/g },
    { name: 'link_start', regex: /\[/g },
    { name: 'link_mid', regex: /\]\(/g },
    { name: 'link_end', regex: /\)/g },
    { name: 'code_start', regex: /`/g },
    { name: 'header', regex: /^#+\s/gm },
    { name: 'list', regex: /^[-*+]\s/gm },
    { name: 'table_separator', regex: /^\|[-:]+\|/gm },
    { name: 'newline', regex: /\n/g },
  ];

  patterns.forEach(({ name, regex }) => {
    let match;
    while ((match = regex.exec(markdown)) !== null) {
      boundaries.push({
        position: match.index,
        type: name,
        text: match[0]
      });
    }
  });

  return boundaries.sort((a, b) => a.position - b.position);
};

/**
 * Test streaming at specific problem boundaries
 */
export const testAtBoundaries = (markdown, renderFn) => {
  const boundaries = findMarkdownBoundaries(markdown);
  const results = [];

  boundaries.forEach(boundary => {
    const partialText = markdown.substring(0, boundary.position + 1);
    try {
      renderFn(partialText);
      results.push({
        ...boundary,
        success: true,
        error: null
      });
    } catch (error) {
      results.push({
        ...boundary,
        success: false,
        error: error.message
      });
      console.error(`Render error at position ${boundary.position} (${boundary.type}):`, error);
    }
  });

  return results;
};